import type { FaireAdPerformancePeriodEntity } from "@faire/playground-entity-db/entity_types/adPerformancePeriod";

import { COPY } from "./copy";
import { monthLabelFromIndex } from "./customersData";
import {
  getActiveProfileToken,
  getP8ActivityMonthlyTotals,
  getP8OverviewPeriods,
  getP8Profile,
  getScopePeriods,
  type P8MonthlyTotals,
} from "./p8AdvertiserData";
import {
  continuationForAgeMonths,
  formatOverviewCount,
  formatOverviewCurrency,
  getCurrentMonthIndex,
  MAX_CONTINUATION,
  monthIndexFromLabel,
} from "./p8OverviewData";
import {
  getRealCohortContinuedSales,
  isRealBrandProfile,
} from "./realBrandData";
import { fillCopy } from "./renderCopy";

/**
 * Per-month conversion cohort data for the "Ad conversions by month" drilldown.
 *
 * A month's acquired customers keep reordering over their first year. For modeled
 * profiles that curve is derived from the cohort's first-order sales via
 * continuationForAgeMonths. Real-brand profiles instead step at the months their
 * reorders actually landed, from the order-level pull in `realBrandData.ts`;
 * cohorts with no attributed reorder fall back to the modeled curve. See
 * snowflakeQueries.ts COHORT_CONTINUATION for the refresh query.
 */

/**
 * Named rather than inlined because the page reorders the metric cards by title
 * and needs to place this one, and both profile branches build it.
 */
export const CONTINUED_TITLE = COPY.cohort.kpi.continuedTitle;

export type MonthDetailKpi = {
  title: string;
  value: string;
  /** Optional +% lift shown as a tag next to the value (First-year reorders). */
  deltaPct?: number | null;
  /** Additional reorder dollars behind the lift, for the tag tooltip. */
  deltaDollars?: number | null;
  footers: string[];
  tooltip: string;
};

/**
 * Cohort-scoped copy for the drilldown's headline metrics. Deliberately not
 * reused from the Overview's, which describe lifetime totals across every
 * advertising month, whereas each figure here describes one month's conversions
 * and how they've compounded since.
 */
export const MONTH_DETAIL_KPI_TOOLTIPS = COPY.cohort.kpi;

/**
 * The same tooltips with the cohort month filled in.
 *
 * They name the month rather than saying "this month" because the page is
 * reached by clicking one row out of a table of twelve, and a reader who has
 * drilled in is usually checking they landed on the one they meant. Trusting
 * the heading for that means every tooltip on the page is only as reliable as
 * the reader's memory of it.
 *
 * Short form, so the tooltips agree with the chart axes beside them; the
 * heading keeps the long form, which reads better as a title than mid-sentence.
 */
export function getMonthDetailKpiTooltips(monthIndex: number) {
  const month = monthLabelFromIndex(monthIndex);
  const kpi = COPY.cohort.kpi;
  return {
    conversions: fillCopy(kpi.conversions, { month }),
    adSpend: fillCopy(kpi.adSpend, { month }),
    immediateAdSales: fillCopy(kpi.immediateAdSales, { month }),
    continuedAdSales: fillCopy(kpi.continuedAdSales, { month }),
    firstYearAdSales: fillCopy(kpi.firstYearAdSales, { month }),
    firstYearAdSalesForecast: fillCopy(kpi.firstYearAdSalesForecast, { month }),
  };
}

function findMonthTotals(monthIndex: number): P8MonthlyTotals | undefined {
  const label = monthLabelFromIndex(monthIndex);
  return getP8ActivityMonthlyTotals().find((totals) => totals.month === label);
}

function getSegmentShares() {
  const overview = getP8Profile().overview;
  const totalConversions = Math.max(1, overview.numTotalConversions);
  const totalSales = Math.max(1, overview.totalAdsSalesDollars);
  const totalSpend = Math.max(1, overview.totalAdsSpendDollars);
  return {
    newConversionShare: overview.numCustomersAcquired / totalConversions,
    newSalesShare: (overview.newCustomerAdsSalesDollars ?? 0) / totalSales,
    newSpendShare: (overview.newCustomerAdsSpendDollars ?? 0) / totalSpend,
  };
}

const LONG_MONTH_LABEL = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
});

/** Full month name for the detail header, e.g. "March 2026". */
export function getMonthDetailLabel(monthIndex: number): string {
  const year = Math.floor(monthIndex / 12);
  const month1 = (monthIndex % 12) + 1;
  return LONG_MONTH_LABEL.format(new Date(year, month1 - 1, 1));
}

/** Calendar month index (year * 12 + monthOfYear) for a real-brand period. */
function periodMonthIndex(period: FaireAdPerformancePeriodEntity): number {
  const [year, month] = period.startDate.split("-").map(Number);
  return year * 12 + (month - 1);
}

function findRealPeriod(
  periods: FaireAdPerformancePeriodEntity[],
  monthIndex: number
): FaireAdPerformancePeriodEntity | undefined {
  return periods.find((period) => periodMonthIndex(period) === monthIndex);
}

/**
 * Real-brand KPIs read straight from the per-period Snowflake values (first-order
 * sales, sales to date, first-year value) rather than the modeled continuation
 * curve, so the top metrics match the platform exactly.
 */
function getRealBrandMonthKpis(monthIndex: number): MonthDetailKpi[] {
  const overview = findRealPeriod(getP8OverviewPeriods(), monthIndex);
  const newP = findRealPeriod(getScopePeriods("NEW_MARKETPLACE"), monthIndex);
  const reorderP = findRealPeriod(
    getScopePeriods("REORDER_MARKETPLACE"),
    monthIndex
  );

  const newConversions = overview?.newCustomersAcquired ?? 0;
  const returningConversions = overview?.reorderConversions ?? 0;
  const conversions = newConversions + returningConversions;

  const adSpend = overview?.adsSpendDollars ?? 0;
  const newSpend = newP?.adsSpendDollars ?? 0;
  const returningSpend =
    reorderP?.adsSpendDollars ?? Math.max(0, adSpend - newSpend);

  const firstOrderSales = overview?.firstOrderSalesDollars ?? 0;
  const newFirstOrder = newP?.firstOrderSalesDollars ?? 0;
  const returningFirstOrder =
    reorderP?.firstOrderSalesDollars ??
    Math.max(0, firstOrderSales - newFirstOrder);

  const salesToDate = overview?.salesToDateDollars ?? firstOrderSales;

  const deltaDollars = Math.max(0, salesToDate - firstOrderSales);
  const hasLift = deltaDollars > 0 && firstOrderSales > 0;
  const tooltips = getMonthDetailKpiTooltips(monthIndex);

  return [
    {
      title: "Conversions",
      value: formatOverviewCount(conversions),
      footers: [
        `${formatOverviewCount(newConversions)} from new customer ads`,
        `${formatOverviewCount(returningConversions)} from reorder ads`,
      ],
      tooltip: tooltips.conversions,
    },
    {
      title: "Ad spend",
      value: formatOverviewCurrency(adSpend),
      footers: [
        `${formatOverviewCurrency(newSpend)} on new customer ads`,
        `${formatOverviewCurrency(returningSpend)} on reorder ads`,
      ],
      tooltip: tooltips.adSpend,
    },
    {
      title: "Ad sales",
      value: formatOverviewCurrency(firstOrderSales),
      footers: [
        `${formatOverviewCurrency(newFirstOrder)} from new customer ads`,
        `${formatOverviewCurrency(returningFirstOrder)} from reorder ads`,
      ],
      tooltip: tooltips.immediateAdSales,
    },
    {
      // No ad-type split: every continued sale comes from a new customer ad, so
      // the two footers would be the value beside a zero. The lift tag carries
      // this figure as a percentage of Ad sales instead.
      title: CONTINUED_TITLE,
      value: formatOverviewCurrency(deltaDollars),
      deltaPct: hasLift
        ? Math.round((deltaDollars / firstOrderSales) * 100)
        : null,
      deltaDollars: hasLift ? deltaDollars : null,
      footers: [],
      tooltip: tooltips.continuedAdSales,
    },
  ];
}

export function getMonthDetailKpis(monthIndex: number): MonthDetailKpi[] {
  if (isRealBrandProfile(getActiveProfileToken())) {
    return getRealBrandMonthKpis(monthIndex);
  }

  const totals = findMonthTotals(monthIndex) ?? {
    month: monthLabelFromIndex(monthIndex),
    adSales: 0,
    adSpend: 0,
    clicks: 0,
    conversions: 0,
  };
  const shares = getSegmentShares();
  const age = getCurrentMonthIndex() - monthIndex;
  const delta = cohortContinuation(age);

  const conversions = totals.conversions;
  const newConversions = Math.round(conversions * shares.newConversionShare);
  const returningConversions = Math.max(0, conversions - newConversions);

  const adSpend = totals.adSpend;
  const newSpend = Math.round(adSpend * shares.newSpendShare);
  const returningSpend = Math.max(0, adSpend - newSpend);

  const firstOrderSales = totals.adSales;
  const newFirstOrder = Math.round(firstOrderSales * shares.newSalesShare);
  const returningFirstOrder = Math.max(0, firstOrderSales - newFirstOrder);

  const salesToDate = Math.round(firstOrderSales * (1 + delta));
  const continuedSales = Math.max(0, salesToDate - firstOrderSales);
  const tooltips = getMonthDetailKpiTooltips(monthIndex);

  return [
    {
      title: "Conversions",
      value: formatOverviewCount(conversions),
      footers: [
        `${formatOverviewCount(newConversions)} from new customer ads`,
        `${formatOverviewCount(returningConversions)} from reorder ads`,
      ],
      tooltip: tooltips.conversions,
    },
    {
      title: "Ad spend",
      value: formatOverviewCurrency(adSpend),
      footers: [
        `${formatOverviewCurrency(newSpend)} on new customer ads`,
        `${formatOverviewCurrency(returningSpend)} on reorder ads`,
      ],
      tooltip: tooltips.adSpend,
    },
    {
      title: "Ad sales",
      value: formatOverviewCurrency(firstOrderSales),
      footers: [
        `${formatOverviewCurrency(newFirstOrder)} from new customer ads`,
        `${formatOverviewCurrency(returningFirstOrder)} from reorder ads`,
      ],
      tooltip: tooltips.immediateAdSales,
    },
    {
      // See the real-brand branch: no ad-type split, and the lift tag rides here
      // rather than on the total.
      title: CONTINUED_TITLE,
      value: formatOverviewCurrency(continuedSales),
      deltaPct: age > 0 ? Math.round(delta * 100) : null,
      deltaDollars: age > 0 ? continuedSales : null,
      footers: [],
      tooltip: tooltips.continuedAdSales,
    },
  ];
}

export type CumulativeSalesPoint = {
  month: string;
  /** Cohort first-order sales (constant baseline across the window). */
  firstOrderSales: number;
  /** Cumulative sales realized so far (solid); null for future months. */
  salesToDate: number | null;
  /**
   * Projected first-year sales, rendered as a single dot at the +12-month mark.
   * Null for every other month, so no line is drawn between "now" and the
   * future projection — reorders don't accrue linearly.
   */
  projectedFirstYearSales: number | null;
  /**
   * Two-point anchor for the dotted projection line: non-null only at the latest
   * realized point and the +12-month projection, so a dashed line visibly
   * connects the present sales-to-date to the projected first-year total. Null
   * everywhere else (and entirely once the cohort is 12+ months old, when the
   * first year is already realized). Not surfaced in the tooltip.
   */
  projectedConnector: number | null;
  /**
   * Order-count twins of the three sales series above, for the chart's Orders
   * metric. Orders ride the same continuation multiplier as sales rather than a
   * curve of their own: the reorder model is `conversions * AOV`, so at a fixed
   * AOV the two move together. Snowflake has no per-cohort order curve either.
   */
  ordersToDate: number | null;
  projectedFirstYearOrders: number | null;
  projectedOrdersConnector: number | null;
  /** Cohort ad spend (constant reference line). */
  adSpend: number;
  /** Cumulative ROAS realized so far (sales to date ÷ ad spend); null for future. */
  roasToDate: number | null;
  /** Projected first-year ROAS, rendered as a single dot at the +12-month mark. */
  projectedRoas: number | null;
  isProjected: boolean;
};

/**
 * Concave ease-out over [0, 1]. Reorders arrive fastest right after acquisition
 * and taper off, so cumulative sales-to-date bends rather than climbing linearly.
 */
function easeOutCumulative(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - clamped, 1.8);
}

/** Additional cumulative reorder lift (fraction of first-order sales) accrued across years 2+. */
const SECOND_YEAR_LIFT = 0.5;
/** Monthly decay of the year-2+ incremental reorders. */
const SECOND_YEAR_DECAY = 0.9;

/**
 * Cumulative reorder continuation (fraction of first-order sales) at a cohort
 * age in months. Within the first year this matches the overview model; beyond
 * 12 months customers keep reordering, so it keeps growing past the first-year
 * total at a tapering, decaying rate.
 */
export function cohortContinuation(age: number): number {
  if (age <= 12) {
    return continuationForAgeMonths(age);
  }
  return (
    MAX_CONTINUATION +
    SECOND_YEAR_LIFT * (1 - Math.pow(SECOND_YEAR_DECAY, age - 12))
  );
}

/**
 * Cumulative sales curve from the acquisition month through the later of the
 * present month or the +12-month first-year mark. Every point is the cohort's
 * sales to date (first-order sales plus continued reorders): the first point is
 * first-order sales, the +12-month point is the projected first-year total, and
 * for cohorts older than a year the realized curve keeps growing to the present.
 */
/** Cohort key for the real reorder-timing lookup, e.g. 24313 -> "2026-02". */
function isoMonthFromIndex(monthIndex: number): string {
  const year = Math.floor(monthIndex / 12);
  const month = (monthIndex % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}

/**
 * Cumulative share of a cohort's realized continued sales at each month of age,
 * from the real per-order reorder timing.
 *
 * Normalized over only the reorders that landed on or before the present month,
 * so the curve still ends exactly on the platform's own sales-to-date figure —
 * the real data sets where the curve steps, not how high it climbs. That keeps
 * the chart reconciled with the KPIs above it, which come from the period tables.
 *
 * Returns null when there's no real timing to draw (the cohort is brand new, or
 * nothing has been attributed to it inside the realized window), leaving the
 * caller on the modeled ease.
 */
function buildRealizedShares(
  continuedByOffset: Record<number, number> | undefined,
  currentAge: number
): number[] | null {
  if (!continuedByOffset || currentAge <= 0) {
    return null;
  }

  const dollarsByAge = Array.from({ length: currentAge + 1 }, () => 0);
  for (const [offset, dollars] of Object.entries(continuedByOffset)) {
    const age = Number(offset);
    if (age >= 1 && age <= currentAge) {
      dollarsByAge[age] += dollars;
    }
  }

  const total = dollarsByAge.reduce((sum, dollars) => sum + dollars, 0);
  if (total <= 0) {
    return null;
  }

  let running = 0;
  return dollarsByAge.map((dollars) => {
    running += dollars;
    return running / total;
  });
}

/**
 * Real-brand cumulative curve anchored to the platform's per-period values:
 * first-order sales at acquisition, the realized sales-to-date at the present
 * month, and the projected first-year total as the +12-month marker dot. Between
 * those anchors it steps at the months this cohort's reorders actually landed,
 * falling back to an eased shape for cohorts with no attributed reorder.
 */
function getRealBrandCumulativeSeries(
  monthIndex: number
): CumulativeSalesPoint[] {
  const overview = findRealPeriod(getP8OverviewPeriods(), monthIndex);
  const firstOrderSales = overview?.firstOrderSalesDollars ?? 0;
  const salesToDatePresent = overview?.salesToDateDollars ?? firstOrderSales;
  const firstYearValue = overview?.salesDollars ?? firstOrderSales;
  const adSpend = overview?.adsSpendDollars ?? 0;
  const conversions =
    (overview?.newCustomersAcquired ?? 0) + (overview?.reorderConversions ?? 0);
  const currentAge = Math.max(0, getCurrentMonthIndex() - monthIndex);
  const horizon = Math.max(12, currentAge);
  const roundRoas = (value: number) => Math.round(value * 10) / 10;

  const realizedShares = buildRealizedShares(
    getRealCohortContinuedSales(
      getActiveProfileToken(),
      isoMonthFromIndex(monthIndex)
    ),
    currentAge
  );

  const realized = (k: number): number => {
    if (currentAge <= 0 || firstOrderSales <= 0) {
      return firstOrderSales;
    }
    const lift = salesToDatePresent - firstOrderSales;
    const share = realizedShares
      ? (realizedShares[Math.min(k, currentAge)] ?? 1)
      : easeOutCumulative(k / currentAge);
    return Math.round(firstOrderSales + lift * share);
  };

  // Orders follow the realized sales curve proportionally, anchored on the
  // period's own conversion count at acquisition.
  const salesMultiple = (value: number) =>
    firstOrderSales > 0 ? value / firstOrderSales : 1;
  const realizedOrders = (k: number) =>
    Math.round(conversions * salesMultiple(realized(k)));
  const firstYearOrders = Math.round(
    conversions * salesMultiple(firstYearValue)
  );

  // Only connect present → projection while the +12-month mark is still ahead.
  const showProjectedConnector = currentAge < 12;
  const presentSalesToDate = realized(currentAge);
  const presentOrdersToDate = realizedOrders(currentAge);

  return Array.from({ length: horizon + 1 }, (_, k) => {
    const isProjected = k > currentAge;
    const realizedRounded = realized(k);

    return {
      month: monthLabelFromIndex(monthIndex + k),
      firstOrderSales,
      salesToDate: isProjected ? null : realizedRounded,
      projectedFirstYearSales: k === 12 ? Math.round(firstYearValue) : null,
      projectedConnector:
        showProjectedConnector && (k === currentAge || k === 12)
          ? k === 12
            ? Math.round(firstYearValue)
            : presentSalesToDate
          : null,
      ordersToDate: isProjected ? null : realizedOrders(k),
      projectedFirstYearOrders: k === 12 ? firstYearOrders : null,
      projectedOrdersConnector:
        showProjectedConnector && (k === currentAge || k === 12)
          ? k === 12
            ? firstYearOrders
            : presentOrdersToDate
          : null,
      adSpend,
      roasToDate:
        isProjected || adSpend <= 0
          ? null
          : roundRoas(realizedRounded / adSpend),
      projectedRoas:
        k === 12 && adSpend > 0 ? roundRoas(firstYearValue / adSpend) : null,
      isProjected,
    };
  });
}

export function getCumulativeSalesSeries(
  monthIndex: number
): CumulativeSalesPoint[] {
  if (isRealBrandProfile(getActiveProfileToken())) {
    return getRealBrandCumulativeSeries(monthIndex);
  }

  const totals = findMonthTotals(monthIndex);
  const firstOrderSales = totals?.adSales ?? 0;
  const adSpend = totals?.adSpend ?? 0;
  const conversions = totals?.conversions ?? 0;
  const currentAge = Math.max(0, getCurrentMonthIndex() - monthIndex);

  // Total continuation realized through the present month (matches the KPI).
  const presentContinuation = cohortContinuation(currentAge);
  // Projected first-year total: the fixed +12-month marker dot.
  const projectedEndpoint = Math.round(
    firstOrderSales * (1 + MAX_CONTINUATION)
  );
  // Track every actual month through the later of the present or +12 months.
  const horizon = Math.max(12, currentAge);

  const roundRoas = (value: number) => Math.round(value * 10) / 10;

  // Realized sales-to-date multiplier at offset k. Stays concave (ease-out),
  // anchored so the present month equals the modeled sales-to-date and the
  // +12-month point lands exactly on the projected first-year total.
  const realizedMultiplier = (k: number): number => {
    if (currentAge <= 0) {
      return 1;
    }
    if (currentAge <= 12) {
      return 1 + presentContinuation * easeOutCumulative(k / currentAge);
    }
    if (k <= 12) {
      return 1 + MAX_CONTINUATION * easeOutCumulative(k / 12);
    }
    const secondYearLift = presentContinuation - MAX_CONTINUATION;
    return (
      1 +
      MAX_CONTINUATION +
      secondYearLift * easeOutCumulative((k - 12) / (currentAge - 12))
    );
  };

  // Orders ride the same multiplier, anchored on the month's conversion count.
  const projectedOrdersEndpoint = Math.round(
    conversions * (1 + MAX_CONTINUATION)
  );

  // Only connect present → projection while the +12-month mark is still ahead.
  const showProjectedConnector = currentAge < 12;
  const presentSalesToDate = Math.round(
    firstOrderSales * realizedMultiplier(currentAge)
  );
  const presentOrdersToDate = Math.round(
    conversions * realizedMultiplier(currentAge)
  );

  return Array.from({ length: horizon + 1 }, (_, k) => {
    const isProjected = k > currentAge;
    const realizedRounded = Math.round(firstOrderSales * realizedMultiplier(k));

    return {
      month: monthLabelFromIndex(monthIndex + k),
      firstOrderSales,
      salesToDate: isProjected ? null : realizedRounded,
      // Single marker dot at the +12-month first-year mark.
      projectedFirstYearSales: k === 12 ? projectedEndpoint : null,
      projectedConnector:
        showProjectedConnector && (k === currentAge || k === 12)
          ? k === 12
            ? projectedEndpoint
            : presentSalesToDate
          : null,
      ordersToDate: isProjected
        ? null
        : Math.round(conversions * realizedMultiplier(k)),
      projectedFirstYearOrders: k === 12 ? projectedOrdersEndpoint : null,
      projectedOrdersConnector:
        showProjectedConnector && (k === currentAge || k === 12)
          ? k === 12
            ? projectedOrdersEndpoint
            : presentOrdersToDate
          : null,
      adSpend,
      // ROAS to date tracks alongside sales to date (realized region only).
      roasToDate:
        isProjected || adSpend <= 0
          ? null
          : roundRoas(realizedRounded / adSpend),
      // Projected first-year ROAS mirrors the projected-sales marker dot.
      projectedRoas:
        k === 12 && adSpend > 0 ? roundRoas(projectedEndpoint / adSpend) : null,
      isProjected,
    };
  });
}

/**
 * Orders a month's cohort has placed beyond the one that acquired them: the
 * realized order count at the present month less the count at acquisition.
 *
 * Read off the cumulative series rather than recomputed from a month's totals,
 * and exported rather than kept on the drilldown, because the Overview's month
 * table annotates first-year reorders (the lift on First-year sales (actual))
 * with the same sentence.
 * The two surfaces sit a click apart on the same number, so a second derivation
 * of "additional orders" is a second answer waiting to be noticed.
 *
 * Null when the series has no realized order counts to difference — a cohort
 * acquired in the month still running has nothing after its first order yet.
 * Callers render the bare percentage in that case rather than a sentence
 * counting zero orders.
 */
export function getCohortAdditionalOrders(monthIndex: number): number | null {
  const series = getCumulativeSalesSeries(monthIndex);
  const baselineOrders = series[0]?.ordersToDate;
  const realized = series.filter((point) => point.salesToDate != null);
  const ordersToDate = realized[realized.length - 1]?.ordersToDate;

  if (baselineOrders == null || ordersToDate == null) {
    return null;
  }
  return Math.max(0, ordersToDate - baselineOrders);
}
